<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catGreen" class="mr-2 flex-shrink-0">{{ icon || "mdi-run" }}</v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip v-if="hasData" x-small color="catGreen" outlined class="ml-2 flex-shrink-0">
        {{ practices.length }}
      </v-chip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" class="flex-shrink-0 ml-1" @click="openDialog">
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar práticas</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!hasData" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-run-fast</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhuma prática registrada ainda.
        </p>
        <v-btn small color="catGreen" outlined class="mt-3" @click="openQuickAdd">
          <v-icon small left>mdi-plus</v-icon>
          Adicionar prática
        </v-btn>
      </div>

      <!-- Resumo -->
      <div v-else class="practices-list">
        <div v-for="(p, i) in practices.slice(0, 3)" :key="i" class="practice-line">
          <v-icon x-small color="catGreen" class="mr-1">mdi-dumbbell</v-icon>
          <span class="font-weight-medium">{{ p.modality || "—" }}</span>
          <v-chip
            x-small
            :color="p.structure_type === 'structured' ? 'green' : 'orange'"
            text-color="white"
            class="ml-2"
          >
            {{ p.structure_type === "structured" ? "Estrut." : "N/estrut." }}
          </v-chip>
          <div class="grey--text practice-sub">
            {{ daysShort(p.days_of_week) }}
            <template v-if="p.start_time">
              · {{ p.start_time }}<span v-if="p.end_time">–{{ p.end_time }}</span>
            </template>
          </div>
        </div>
        <div v-if="practices.length > 3" class="text-center mt-2">
          <v-chip small outlined color="catGreen" @click="openDialog">
            +{{ practices.length - 3 }}
            {{ practices.length - 3 === 1 ? "prática" : "práticas" }}
          </v-chip>
        </div>
      </div>
    </v-card-text>

    <EditExercisePracticesDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :practices="practices"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="fetchData"
    />
  </v-card>
</template>

<script>
import http from "@/services/http";
import EditExercisePracticesDialog from "@/features/patients/profile-dashboard/dialogs/EditExercisePracticesDialog.vue";

const DAY_OPTIONS = [
  { label: "Seg", value: "mon" },
  { label: "Ter", value: "tue" },
  { label: "Qua", value: "wed" },
  { label: "Qui", value: "thu" },
  { label: "Sex", value: "fri" },
  { label: "Sáb", value: "sat" },
  { label: "Dom", value: "sun" },
];

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "ExercisePracticeCard",

  components: { EditExercisePracticesDialog },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: "mdi-run" },
  },

  data() {
    return {
      dialogVisible: false,
      practices: [],
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.practices || []).length;
    },
    hasData() {
      return this.practices.length > 0;
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        const { data } = await http.get(
          `/api/exercise-practices/by-patient/${this.patientId}`,
        );
        this.practices = data?.data?.practices || [];
      } catch (err) {
        console.error("Erro ao buscar práticas de exercício:", err);
        this.practices = [];
      }
    },

    daysShort(days) {
      if (!Array.isArray(days) || days.length === 0) return "—";
      return days
        .map((d) => DAY_OPTIONS.find((o) => o.value === d)?.label || d)
        .join(", ");
    },

    openDialog() {
      this.dialogVisible = true;
    },

  },
};
</script>

<style scoped>
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid #bbf7d0 !important;
  box-shadow: 0 2px 10px rgba(21, 128, 61, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.card-fixed-height:hover {
  border-color: #86efac !important;
  box-shadow: 0 6px 20px rgba(21, 128, 61, 0.18) !important;
}
.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: var(--ds-soft-green);
  border-bottom: 1px solid #bbf7d0;
}
.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-cat-green);
  letter-spacing: 0.3px;
}
.card-content {
  flex: 1;
  overflow-y: auto;
}
.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #86efac;
  border-radius: 10px;
}
.practices-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.practice-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 13px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #dcfce7;
}
.practice-sub {
  flex-basis: 100%;
  font-size: 11px;
  margin-top: 2px;
}
</style>
